/*
 * BNO cookie consent banner.
 *
 * Drives the glass banner rendered by sections/bno-cookie-banner.liquid using
 * Shopify's Customer Privacy API — we never store consent ourselves. The banner
 * is shown ONLY when shouldShowBanner() returns true, and setTrackingConsent is
 * called ONLY in direct response to a button click (calling it on load would
 * invalidate consent under the GDPR / ePrivacy Directive). If the API fails to
 * load we log and show nothing — never a banner whose buttons do nothing.
 */

/*
 * Drawer-aware visibility for the cookie banner (a pure visibility concern; never
 * touches recorded consent).
 *
 * The banner is position: fixed at z-index 200 and, for the non-modal cart drawer,
 * sits ABOVE the drawer — covering its Total / Checkout. So it must hide while ANY
 * drawer is open and reappear when the last one closes.
 *
 * ONE mechanism: a single MutationObserver watches the two DOM truths the theme's
 * shared drawer infrastructure already sets — the native `open` attribute on any
 * <dialog> (cart, search, quick-add, image zoom) and the `scroll-lock` attribute
 * on <html> (the <details> hamburger, via dialog.js). It recomputes "is any drawer
 * open?" from the DOM (not a counter), so swapping straight from one drawer to
 * another stays hidden throughout and the banner reappears only when the last
 * drawer closes.
 *
 * The fade lives in CSS (html.bno-drawer-open ...). Screen-reader hiding is
 * SEQUENCED against that fade: inert + aria-hidden are added only AFTER the 0.42s
 * fade-out has finished (so opacity:0 never leaves the banner announced), and
 * removed BEFORE the fade-in starts, so neither animation is cut short. Under
 * reduced motion (no transition) the a11y hide is applied immediately.
 */
(function () {
  var docEl = document.documentElement;
  var banner = document.getElementById('BnoCookieBanner');
  if (!banner) return;

  var reduce =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Matches the .bno-cookie__inner transition (0.42s) plus the same small buffer
  // the banner's own hide() uses, so the a11y hide lands just after the fade-out.
  var FADE_MS = 460;
  var hideTimer = null;
  var isOpen = false;

  function anyDrawerOpen() {
    return (
      document.querySelector('dialog[open]') != null || docEl.hasAttribute('scroll-lock')
    );
  }

  function setA11yHidden(hidden) {
    if (hidden) {
      banner.setAttribute('inert', '');
      banner.setAttribute('aria-hidden', 'true');
    } else {
      banner.removeAttribute('inert');
      banner.removeAttribute('aria-hidden');
    }
  }

  function sync() {
    var open = anyDrawerOpen();
    if (open === isOpen) return; // only act on an actual open<->closed transition
    isOpen = open;

    if (open) {
      // Start the fade-out (CSS reacts to the class); remove from the a11y tree
      // only AFTER it finishes so opacity:0 never leaves the banner announced.
      docEl.classList.add('bno-drawer-open');
      if (hideTimer) clearTimeout(hideTimer);
      if (reduce) {
        setA11yHidden(true);
      } else {
        hideTimer = window.setTimeout(function () {
          hideTimer = null;
          if (anyDrawerOpen()) setA11yHidden(true);
        }, FADE_MS);
      }
    } else {
      // Restore to the a11y tree BEFORE the fade-in, cancelling any pending hide.
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      setA11yHidden(false);
      docEl.classList.remove('bno-drawer-open');
    }
  }

  new MutationObserver(sync).observe(docEl, {
    subtree: true,
    attributes: true,
    attributeFilter: ['open', 'scroll-lock'],
  });

  // Cover the (unlikely) case of a drawer already open at script load.
  sync();
})();

(function () {
  var root = document.getElementById('BnoCookieBanner');
  if (!root) return;

  var acceptBtn = root.querySelector('.bno-cookie__accept');
  var declineBtn = root.querySelector('.bno-cookie__decline');
  var closeBtn = root.querySelector('.bno-cookie__close');
  if (!acceptBtn || !declineBtn) return;

  var reduce =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // dismissible is true ONLY while the banner is reopened via the footer's
  // "Cookie preferences" control — there a prior choice already exists to fall
  // back on, so closing without choosing is safe. It gates both the close "×"
  // (revealed by CSS via [data-dismissible]) and the Escape key (dismiss() is a
  // no-op otherwise). The first-time banner never sets it: a new visitor must
  // accept or decline, and cannot dismiss.
  var dismissible = false;
  var handlersWired = false;

  function setDismissible(on) {
    dismissible = on;
    if (on) {
      root.setAttribute('data-dismissible', 'true');
    } else {
      root.removeAttribute('data-dismissible');
    }
  }

  function show() {
    root.hidden = false;
    if (reduce) {
      root.setAttribute('data-shown', 'true');
      return;
    }
    // Two frames so the browser paints the hidden state before transitioning in.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        root.setAttribute('data-shown', 'true');
      });
    });
  }

  function hide() {
    if (reduce) {
      root.hidden = true;
      return;
    }
    root.setAttribute('data-shown', 'false');
    // Match the CSS transition duration (0.42s) plus a small buffer, then remove
    // from layout / tab order entirely.
    window.setTimeout(function () {
      root.hidden = true;
    }, 460);
  }

  // Close WITHOUT recording consent. Allowed only when the banner was reopened
  // (dismissible), so a first-time visitor can never escape a decision. Leaves
  // the previously recorded consent completely untouched — no API call at all.
  function dismiss() {
    if (!dismissible) return;
    setDismissible(false);
    hide();
  }

  // consent: boolean applied to all three categories (all-or-nothing banner).
  // The ONLY path that writes consent; runs strictly from a button click.
  function record(consent) {
    window.Shopify.customerPrivacy.setTrackingConsent(
      { marketing: consent, analytics: consent, preferences: consent },
      function () {
        setDismissible(false); // a choice was made; leave reopened mode
        hide();
      }
    );
  }

  // Wire the accept / decline / close controls exactly once, only ever after the
  // API is confirmed live, so a click can never fire against a missing API.
  function wireHandlers() {
    if (handlersWired) return;
    handlersWired = true;
    acceptBtn.addEventListener('click', function () {
      record(true);
    });
    declineBtn.addEventListener('click', function () {
      record(false);
    });
    if (closeBtn) closeBtn.addEventListener('click', dismiss);
    // Escape dismisses only a reopened banner (dismiss() no-ops otherwise).
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') dismiss();
    });
  }

  // Load Shopify's Customer Privacy API at most once; queue callers until it
  // resolves. cb(true) when the API is live, cb(false) on any failure.
  var loadState = 'idle'; // idle | loading | ready | failed
  var waiters = [];
  function flush(ok) {
    var pending = waiters;
    waiters = [];
    for (var i = 0; i < pending.length; i++) pending[i](ok);
  }
  function ensureApi(cb) {
    if (loadState === 'ready') return cb(true);
    if (loadState === 'failed') return cb(false);
    waiters.push(cb);
    if (loadState === 'loading') return;

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      loadState = 'failed';
      console.error('[bno-cookie] Shopify.loadFeatures unavailable; banner not shown.');
      return flush(false);
    }
    loadState = 'loading';
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        var privacy = window.Shopify && window.Shopify.customerPrivacy;
        if (
          error ||
          !privacy ||
          typeof privacy.shouldShowBanner !== 'function' ||
          typeof privacy.currentVisitorConsent !== 'function'
        ) {
          loadState = 'failed';
          console.error(
            '[bno-cookie] consent-tracking-api unavailable:',
            error || 'customerPrivacy API missing after load'
          );
          return flush(false);
        }
        loadState = 'ready';
        flush(true);
      }
    );
  }

  // Public reopen entry point. Bypasses the consent gate on purpose so a visitor
  // can revisit their choice at any time, but NEVER records consent by itself —
  // it only shows the banner; accept/decline still do the writing. Reopening
  // makes the banner dismissible so closing without choosing keeps the old
  // choice intact.
  function open() {
    ensureApi(function (ok) {
      if (!ok) return; // never show a banner whose buttons cannot record consent
      wireHandlers();
      setDismissible(true);
      show();
    });
  }

  // Expose to the footer's "Cookie preferences" control. Delegated so it works
  // on every page regardless of where/when that control renders, and a global
  // hook is available for anything else that needs to reopen the banner.
  window.BnoCookieBanner = { open: open };
  document.addEventListener('click', function (e) {
    var trigger =
      e.target && e.target.closest && e.target.closest('[data-bno-cookie-preferences]');
    if (!trigger) return;
    e.preventDefault();
    open();
  });

  // First-time auto-show. Respects BOTH gates and is NEVER dismissible.
  ensureApi(function (ok) {
    if (!ok) return;
    var privacy = window.Shopify.customerPrivacy;

    // Gate 1: shouldShowBanner() only reports whether this visitor's REGION is
    // configured to show a banner. It does NOT reflect a prior choice, so it
    // stays true after accept/decline. Keeps us silent where no banner is due.
    if (!privacy.shouldShowBanner()) return;

    // Gate 2: currentVisitorConsent() is the actual recorded decision. Observed
    // runtime shape: { analytics, marketing, preferences, sale_of_data }, each
    // "yes" | "no" | "" (empty = no decision). sale_of_data stays "" even for a
    // fully-decided visitor outside a sale-of-data region, so a decision exists
    // iff ANY of the three categories this banner sets is non-empty. Without
    // this gate the banner re-appears on every navigation after a choice.
    // The footer reopen path deliberately bypasses this gate; the auto-show does
    // not, so a decided visitor still never sees the banner on a normal load.
    var consent = privacy.currentVisitorConsent();
    if (consent && (consent.analytics || consent.marketing || consent.preferences)) {
      return; // Visitor has already accepted or declined; stay hidden.
    }

    wireHandlers();
    // dismissible stays false: first-time visitor must accept or decline.
    show();
  });
})();
